/** @jsx jsx **/
import { jsx, Box, Flex, Text, Close } from 'theme-ui'
import { useState, Fragment, useCallback, useEffect, useRef } from 'react'
import FilterHeader from '~/components/filter/filter-header'
import FilterCategory from '~/components/filter/filter-category'
import FilterReset from '~/components/filter/filter-reset'
import { FilterCategory as FilterCategoryType } from '~/@types/models'
import { space, headerHeight } from '~/gatsby-plugin-theme-ui'
import { useFilterContext } from '~/context/filters'
import Affix from '@uiw/react-affix'

export interface IFilterItem {
  name: string
  value: string
  label: string
  categoryName: string
}

type Props = {
  categories: FilterCategoryType[]
  resetFilters?: () => void
  itemsCount?: number
}
export enum EFilterName {
  filter = 'bsolFilters',
}

export const FilterSelection = ({ resetFilters, categories, itemsCount }: Props) => {
  const {
    setSelectedFilters,
    selectedFilters,
    infiniteScrollRef,
    toggleValue,
    showFilter,
    isMobile,
    isTablet,
    setShowFilter,
  } = useFilterContext()
  const [showMoreFilters, setShowMoreFilters] = useState<boolean>(false)

  const handleShowFilter = () => {
    setShowFilter(!showFilter)
  }

  const handleResetFilters = useCallback(() => {
    setSelectedFilters({})
    setShowMoreFilters(false)
    if (resetFilters) {
      resetFilters()
    }
    localStorage.removeItem(EFilterName.filter)

    if (infiniteScrollRef?.current) {
      infiniteScrollRef.current.props.next()
    }
  }, [setSelectedFilters, selectedFilters, infiniteScrollRef])

  return (
    <Box
      sx={{
        '.w-affix': {
          zIndex: 50,
          backgroundColor: 'white',
        },
      }}
    >
      <Affix
        offsetTop={isMobile ? headerHeight[0] : 0}
        style={{
          zIndex: 50,
          width: '100%',
          left: 0,
          paddingLeft: '16px',
          paddingRight: '16px',
          backgroundColor: 'white',
          minWidth: '282px',
        }}
      >
        <FilterHeader
          handleShowFilter={handleShowFilter}
          handleResetFilters={handleResetFilters}
          showFilter={showFilter}
          itemsCount={itemsCount}
        />
        <Fragment>
          <Box
            sx={{
              zIndex: [50, null, null, 1],
              display: [!showFilter ? 'none' : 'block', null, null, 'block'],
              height: [!showFilter ? 0 : 'auto', null, 'auto'],
              width: ['100%', null, null, '250px'],
              top: '0px',
              backgroundColor: !showFilter ? 'white' : 'transparent',
            }}
          >
            <Box
              sx={{
                position: ['absolute', null, null, 'relative'],
                display: ['none', null, null, 'block'],
                top: ['0px', null, null, 'auto'],
                left: 0,
                width: ['100%', null, null, '250px'],
                maxWidth: ['100%', null, null, '250px'],
                backgroundColor: 'white',
                height: showFilter ? 'auto' : '80vh',
                padding: 0,
                pl: [showFilter ? '20px' : '0', null, null, '20px'],
              }}
            >
              {typeof document !== 'undefined' && (
                <Box
                  className="filter-container"
                  sx={{
                    mt: 0,
                    mb: [space.sm],
                  }}
                >
                  <Flex
                    sx={{
                      // paddingTop: !isMobile ? '25px' : 0,
                      flexWrap: 'wrap',
                      flexDirection: 'column',
                      width: '100%',
                      div: {
                        marginLeft: isMobile ? null : '0px',
                      },
                      // 'div:first-child': {
                      //   marginRight: isMobile ? null : '0px',
                      // },
                      justifyContent: 'center',
                    }}
                  >
                    <Flex
                      sx={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        textTransform: 'uppercase',
                        padding: '11px 0',
                      }}
                    >
                      <Text
                        sx={{
                          fontWeight: 400,
                          color: '#041826',
                          fontSize: '16px',
                          letterSpacing: '0.5px',
                        }}
                      >
                        Filters
                      </Text>
                      <FilterReset handleClickResetFilters={handleResetFilters} />
                    </Flex>
                    <Box
                      sx={{
                        width: '100%',
                        pt: '6px',
                        pr: ['20px', null, null, '0'],
                        height: ['80vh', null, null, '100vh'],
                        overflow: 'auto',
                      }}
                    >
                      {categories.map(category => (
                        <FilterCategory
                          key={category.name}
                          category={category}
                          selectedValues={selectedFilters ? selectedFilters[category.name] : []}
                          handleChange={toggleValue}
                          showMoreFilters={showMoreFilters}
                        />
                      ))}
                    </Box>
                  </Flex>
                </Box>
              )}
            </Box>
          </Box>
        </Fragment>
      </Affix>

      <Box
        data-state-open={showFilter}
        sx={{
          zIndex: 1000,
          position: ['fixed', null, null, 'unset'],
          display: showFilter ? 'block' : 'none',
          height: showFilter ? '100vh' : 0,
          width: ['100%', null, null, 0],
          maxWidth: '100vw',
          top: 0,
          left: 0,
          backgroundColor: 'white',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'block',
            top: ['0px', null, null, 'auto'],
            left: 0,
            zIndex: 100,
            width: ['100%', null, null, '250px'],
            maxWidth: ['100%', null, null, '250px'],
            backgroundColor: 'white',
            height: showFilter ? 'auto' : '80vh',
            padding: 0,
          }}
        >
          {showFilter ? (
            <button
              onClick={() => {
                setShowFilter(false)
              }}
              type="button"
              sx={{
                variant: 'buttons.noStyle',
                position: 'absolute',
                right: '8px',
                top: '4px',
                pl: '8px',
                display: ['block', null, null, 'none'],
                svg: {
                  fill: '#000',
                },
              }}
            >
              <Close />
            </button>
          ) : null}

          {showFilter && typeof document !== 'undefined' && (
            <Box
              className="filter-container"
              sx={{
                mt: 0,
                mb: [space.sm],
              }}
            >
              <Flex
                sx={{
                  flexWrap: 'wrap',
                  flexDirection: 'column',
                  width: '100%',
                  div: {
                    marginLeft: null,
                  },
                  justifyContent: 'center',
                }}
              >
                <Flex
                  sx={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    textTransform: 'uppercase',
                    padding: '11px 0',
                    marginTop: '40px',
                    px: '8px',
                  }}
                >
                  <Text
                    sx={{
                      fontWeight: 400,
                      color: '#041826',
                      fontSize: '16px',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Filters
                  </Text>
                  <FilterReset handleClickResetFilters={handleResetFilters} />
                </Flex>
                <Box
                  sx={{
                    width: '100%',
                    pt: '6px',
                    px: ['8px', null, null, '0'],
                    height: ['80vh', null, null, '100vh'],
                    overflow: 'auto',
                  }}
                >
                  {categories.map(category => (
                    <FilterCategory
                      key={category.name}
                      category={category}
                      selectedValues={selectedFilters ? selectedFilters[category.name] : []}
                      handleChange={toggleValue}
                      showMoreFilters={showMoreFilters}
                    />
                  ))}
                </Box>
              </Flex>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
